import { useEffect, useMemo, useRef, useState } from 'react';
import { ColDef } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';

import { useVisualizationOptionsContext } from '../../../../../context';
import { VisualizationTableProps } from '../models';

import { useAgGridOptions } from './ag-grid';
import { makeColumns } from './make-columns';
import { useSearch } from './use-search';

type UseVisualizationTableProps = Pick<
    VisualizationTableProps,
    'options' | 'activeControls' | 'params' | 'data' | 'meta' | 'executionId' | 'editing' | 'id'
>;

const useVisualizationTable = ({
    id,
    options,
    activeControls,
    params,
    data,
    meta,
    executionId,
    editing,
}: UseVisualizationTableProps) => {
    const [columnTypes, setColumnTypes] = useState<any>({});
    const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
    const { activeColumns, activeGroup } = useMemo(() => {
        const activeGroup = params?.groups ?? activeControls?.groups?.initialValue;
        const activeColumns = options.groups?.find?.(group => group.name === activeGroup)?.columns;
        const fallbackColumns = options.groups?.[0]?.columns;

        return {
            activeColumns: activeColumns ?? fallbackColumns ?? [],
            activeGroup,
        };
    }, [activeControls?.groups?.initialValue, options.groups, params?.groups]);

    const gridRef = useRef<AgGridReact | null>(null);
    const { features } = useVisualizationOptionsContext();

    useEffect(() => {
        setColumnDefs([...makeColumns(activeColumns, columnTypes, options)]);
    }, [activeColumns, columnTypes, editing, options]);
    useEffect(() => {
        /**
         * trigger synthetic server-side refresh so column widths are updated
         *
         * in more detail: this will trigger when a table is being edited or when parent's queries are refetched,
         * then the useEffect above this one will trigger an update to columnDefs. Next,
         * this effect can run and refresh the server side datasource.
         */
        gridRef.current?.api?.refreshServerSide();
    }, [columnDefs]);

    const { agGridProps, serverSideDatasource, clientSideRowData } = useAgGridOptions({
        options,
        data,
        meta,
        executionId,
        columnTypes,
        setColumnTypes,
        gridRef,
        id,
    });

    const { handleSearch, search } = useSearch({ activeGroup, grid: gridRef.current });

    return {
        features,
        handleSearch,
        search,
        showSearch: options?.enableSearch !== false,
        /** ag-grid props */
        columnDefs,
        agGridProps,
        gridRef,
        serverSideDatasource,
        clientSideRowData,
    };
};

export { useVisualizationTable };
